import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  Progress,
  Badge,
  Input,
  Label
} from "reactstrap";
import classnames from "classnames";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const KaliteOzeti = () => {
  document.title = "Kalite Özeti | Lojgo";

  // 1. KULLANICI VE ROL KONTROLLERİ
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const userId = authUser?.ID;
  const isAdmin = authUser?.Users_Type_ID === 3; // 3: Admin

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("ALL"); // "ALL" | "WITH_COMMENT" | "FIVE" | "FOUR_PLUS"

  // 2. ADMİN İÇİN KULLANICI SEÇİM STATE'LERİ
  const [targetUserId, setTargetUserId] = useState(userId); 
  const [usersList, setUsersList] = useState([]);
  const [fullName, setFullName] = useState(authUser?.Users_NameSurname || "Kullanıcı");

  // Data States
  const [averageScore, setAverageScore] = useState(0);
  const [totalRatings, setTotalRatings] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  const [comments, setComments] = useState([]);

  // 3. ADMİN İSE TÜM KULLANICILARI ÇEK (Dropdown İçin)
  useEffect(() => {
    if (isAdmin) {
      fetch("https://hiflowapi.stechomeyazilim.info/Users?$expand=Users_Type")
        .then(res => res.json())
        .then(data => setUsersList(data.value || []))
        .catch(err => console.error("Kullanıcı listesi alınamadı:", err));
    }
  }, [isAdmin]);

  // GERÇEK VERİTABANINDAN ÇEKME İŞLEMİ (OData API)
  const loadData = useCallback(async () => {
    if (!targetUserId) return;
    
    setLoading(true);
    try {
      setError("");

      // userId yerine hedef kullanıcının (targetUserId) puanlarını çekiyoruz
      const url = `https://hiflowapi.stechomeyazilim.info/Advert_Rating?$filter=Evaluated_User_ID eq ${targetUserId}&$expand=Users&$orderby=Created_DateTime desc`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
          throw new Error("Veriler çekilirken sunucu hatası oluştu.");
      }

      const result = await response.json();
      const ratingsData = result.value || [];

      // 1. Toplam Puanlama Sayısı
      const total = ratingsData.length;
      setTotalRatings(total);

      if (total > 0) {
        // 2. Ortalama Hesaplama
        const sumScore = ratingsData.reduce((acc, curr) => acc + (curr.Score || 0), 0);
        setAverageScore(sumScore / total);

        // 3. Yorum Sayısı (Boş olmayanlar)
        const withComments = ratingsData.filter(r => r.Comment && r.Comment.trim() !== "");
        setCommentCount(withComments.length);

        // 4. Ekrana Basılacak Formata Çevirme
        const formattedComments = ratingsData.map(item => ({
            id: item.ID,
            evaluatorName: item.Users?.Users_NameSurname || "İsimsiz Kullanıcı", 
            createdDateTime: item.Created_DateTime,
            advertId: item.Advert_ID,
            score: item.Score || 0,
            comment: item.Comment || ""
        }));

        setComments(formattedComments);
      } else {
        // Hiç puanı yoksa sıfırla
        setAverageScore(0);
        setCommentCount(0);
        setComments([]);
      }

    } catch (err) {
      console.error("QUALITY SCREEN LOAD ERROR:", err);
      setError(err?.message || "Puan bilgileri yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [targetUserId]); // BAĞIMLILIK userId YERİNE targetUserId OLDU

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ADMİN KULLANICI DEĞİŞTİRDİĞİNDE ÇALIŞACAK FONKSİYON
  const handleUserChange = (e) => {
    const selectedId = e.target.value;
    setTargetUserId(selectedId);

    if (selectedId === userId?.toString()) {
        setFullName(authUser?.Users_NameSurname || "Kullanıcı");
    } else {
        const selectedUser = usersList.find(u => u.ID.toString() === selectedId);
        if (selectedUser) {
            setFullName(selectedUser.Users_NameSurname || selectedUser.Users_Name || `Kullanıcı #${selectedId}`);
        }
    }
  };

  // UI Hesaplamaları
  const progressPercent = useMemo(() => {
    return Math.max(0, Math.min(100, averageScore * 20));
  }, [averageScore]);

  const medalText = useMemo(() => {
    if (averageScore >= 4.5) return "ÜSTÜN BAŞARI";
    if (averageScore >= 3.5) return "BAŞARILI";
    if (averageScore >= 2.5) return "ORTALAMA";
    if (averageScore > 0) return "GELİŞİMDE";
    return "DEĞERLENDİRME YOK";
  }, [averageScore]);

  const scoreDescription = useMemo(() => {
    if (totalRatings === 0) return "Bu kullanıcıya ait bir değerlendirme bulunmuyor.";
    return `${totalRatings} puanlama ve ${commentCount} yorum üzerinden ortalama hesaplandı.`;
  }, [totalRatings, commentCount]);

  const filteredComments = useMemo(() => {
    switch (filter) {
      case "WITH_COMMENT":
        return comments.filter((item) => item.comment && item.comment.trim().length > 0);
      case "FIVE":
        return comments.filter((item) => item.score === 5);
      case "FOUR_PLUS":
        return comments.filter((item) => item.score >= 4);
      case "ALL":
      default:
        return comments;
    }
  }, [comments, filter]);

  const formatDate = (dateString) => {
    if (!dateString) return "Tarih yok";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "Tarih yok";
    return date.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });
  };

  const renderStars = (score) => {
    return [...Array(5)].map((_, i) => (
      <i key={i} className={`mdi mdi-star ${i < score ? 'text-warning' : 'text-light'} fs-5 me-1`}></i>
    ));
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Lojgo" breadcrumbItem="Kalite Özeti" />

          {/* SADECE ADMİNLER İÇİN KULLANICI SEÇİM ALANI */}
          {isAdmin && (
            <Row className="mb-4">
              <Col md={12} lg={8} xl={6}>
                <Card className="shadow-sm border border-info rounded-4 mb-0">
                  <CardBody className="py-3 px-4">
                    <Label className="fw-bold text-info mb-2 d-flex align-items-center">
                      <i className="mdi mdi-shield-account fs-4 me-2"></i> Admin Paneli: Kullanıcı Profili Seçimi
                    </Label>
                    <Input 
                        type="select" 
                        value={targetUserId} 
                        onChange={handleUserChange} 
                        className="bg-soft-info border-info fw-bold text-dark cursor-pointer"
                    >
                      <option value={userId}>Kendiniz ({authUser?.Users_NameSurname})</option>
                      {usersList.filter(u => u.ID !== userId).map(u => (
                        <option key={u.ID} value={u.ID}>
                          {u.Users_NameSurname || u.Users_Name} - ({u.Users_Type?.Users_Type_Title || "Bilinmeyen Rol"})
                        </option>
                      ))}
                    </Input>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          <Row>
            {/* SOL TARAF: KAHRAMAN (HERO) KARTI VE İSTATİSTİKLER */}
            <Col xl={4} lg={5}>
              <Card className="shadow-sm border-0 rounded-4 mb-4">
                <CardBody className="p-4">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <Badge color="warning" className="bg-soft-warning text-warning px-3 py-2 font-size-12 rounded-pill d-flex align-items-center">
                      <i className="mdi mdi-trophy me-2 fs-5"></i> {medalText}
                    </Badge>
                    <i className="mdi mdi-information text-muted fs-4" title="Puanlama Bilgisi"></i>
                  </div>

                  <h4 className="card-title text-dark fw-bold mb-4">{fullName}</h4>

                  <div className="d-flex align-items-end mb-2">
                    <h1 className="display-4 fw-900 text-dark mb-0 me-2">{loading ? "..." : averageScore.toFixed(1)}</h1>
                    <div className="pb-1">
                      <span className="text-muted fw-bold fs-5">/ 5.0</span>
                      <div className="mt-1">
                        <Badge color="warning" className="bg-soft-warning text-warning">
                          <i className="mdi mdi-comment-text-outline me-1"></i> {commentCount} Yorum
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <p className="text-muted fw-medium mb-4">{loading ? "Yükleniyor..." : scoreDescription}</p>

                  <div className="mb-4">
                    <Progress value={progressPercent} color="info" className="rounded-pill" style={{ height: "8px" }} />
                    <div className="d-flex justify-content-between mt-2 text-muted fw-bold font-size-11">
                      <span>1</span>
                      <span>3</span>
                      <span>5</span>
                    </div>
                  </div>

                  {/* STAT KARTLARI */}
                  <Row className="g-3">
                    <Col xs={4}>
                      <div className="bg-light rounded-3 p-3 text-center border">
                        <div className="avatar-sm mx-auto mb-2">
                          <span className="avatar-title rounded-circle bg-soft-warning text-warning fs-4">
                            <i className="mdi mdi-star"></i>
                          </span>
                        </div>
                        <h5 className="font-size-15 mb-1 text-dark fw-bold">{averageScore.toFixed(1)}</h5>
                        <p className="text-muted mb-0 font-size-11 fw-bold text-uppercase">Puan</p>
                      </div>
                    </Col>
                    <Col xs={4}>
                      <div className="bg-light rounded-3 p-3 text-center border">
                        <div className="avatar-sm mx-auto mb-2">
                          <span className="avatar-title rounded-circle bg-soft-primary text-primary fs-4">
                            <i className="mdi mdi-calendar-check"></i>
                          </span>
                        </div>
                        <h5 className="font-size-15 mb-1 text-dark fw-bold">{totalRatings}</h5>
                        <p className="text-muted mb-0 font-size-11 fw-bold text-uppercase">Görev</p>
                      </div>
                    </Col>
                    <Col xs={4}>
                      <div className="bg-light rounded-3 p-3 text-center border">
                        <div className="avatar-sm mx-auto mb-2">
                          <span className="avatar-title rounded-circle bg-soft-success text-success fs-4">
                            <i className="mdi mdi-comment-text-outline"></i>
                          </span>
                        </div>
                        <h5 className="font-size-15 mb-1 text-dark fw-bold">{commentCount}</h5>
                        <p className="text-muted mb-0 font-size-11 fw-bold text-uppercase">Yorum</p>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {/* SAĞ TARAF: YORUMLAR LİSTESİ VE FİLTRELER */}
            <Col xl={8} lg={7}>
              <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <h4 className="card-title fw-bold mb-0">Yorumlar</h4>
                
                {/* FİLTRE BUTONLARI */}
                <Nav pills className="custom-nav-pills gap-2">
                  <NavItem>
                    <NavLink className={classnames({ active: filter === "ALL", "cursor-pointer fw-bold border bg-white": filter !== "ALL" })} onClick={() => setFilter("ALL")}>
                      Tümü
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink className={classnames({ active: filter === "WITH_COMMENT", "cursor-pointer fw-bold border bg-white": filter !== "WITH_COMMENT" })} onClick={() => setFilter("WITH_COMMENT")}>
                      Yorumlu
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink className={classnames({ active: filter === "FIVE", "cursor-pointer fw-bold border bg-white": filter !== "FIVE" })} onClick={() => setFilter("FIVE")}>
                      5 Yıldız
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink className={classnames({ active: filter === "FOUR_PLUS", "cursor-pointer fw-bold border bg-white": filter !== "FOUR_PLUS" })} onClick={() => setFilter("FOUR_PLUS")}>
                      4+
                    </NavLink>
                  </NavItem>
                </Nav>
              </div>

              {/* YORUMLAR LİSTESİ */}
              {loading ? (
                <Card className="shadow-none border text-center py-5 rounded-4">
                  <CardBody>
                    <Spinner color="primary" />
                    <h5 className="mt-3 text-muted">Değerlendirmeler yükleniyor...</h5>
                  </CardBody>
                </Card>
              ) : error ? (
                <Card className="shadow-none border text-center py-5 rounded-4">
                  <CardBody>
                    <i className="mdi mdi-alert-circle-outline display-4 text-danger mb-3"></i>
                    <h5 className="text-dark fw-bold">Veri alınamadı</h5>
                    <p className="text-muted">{error}</p>
                  </CardBody>
                </Card>
              ) : filteredComments.length === 0 ? (
                <Card className="shadow-none border text-center py-5 rounded-4 bg-light">
                  <CardBody>
                    <i className="mdi mdi-comment-remove-outline display-4 text-muted mb-3"></i>
                    <h5 className="text-dark fw-bold">Yorum bulunamadı</h5>
                    <p className="text-muted">Bu filtreye uygun değerlendirme yok.</p>
                  </CardBody>
                </Card>
              ) : (
                filteredComments.map((item, index) => (
                  <Card key={item.id || index} className="shadow-sm border-0 rounded-4 mb-3">
                    <CardBody className="p-4">
                      <div className="d-flex align-items-start">
                        {/* Sol İkon Alanı */}
                        <div className="me-3 text-center">
                          <div className="avatar-md">
                            <span className="avatar-title rounded-circle bg-soft-info text-info fs-3">
                              <i className="mdi mdi-account"></i>
                            </span>
                          </div>
                        </div>

                        {/* Yorum İçeriği */}
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <div>
                              <h5 className="font-size-15 fw-bold text-dark mb-1">{item.evaluatorName}</h5>
                              <p className="text-muted font-size-12 mb-0 fw-medium">
                                {formatDate(item.createdDateTime)} 
                                {item.advertId ? <span className="ms-2 border-start ps-2">Görev ID: #{item.advertId}</span> : ""}
                              </p>
                            </div>
                            <Badge color="warning" className="bg-soft-warning text-warning px-2 py-1 font-size-13 d-flex align-items-center">
                              <i className="mdi mdi-star me-1"></i> {item.score.toFixed(1)}
                            </Badge>
                          </div>

                          <div className="mb-3">
                            {renderStars(item.score)}
                          </div>

                          <div className="pt-3 border-top border-light d-flex justify-content-between align-items-center">
                            <p className="text-dark font-size-14 fw-medium mb-0" style={{lineHeight: "1.6"}}>
                              {item.comment?.trim() || <span className="text-muted fst-italic">Yorum bırakılmadı.</span>}
                            </p>
                            <i className="mdi mdi-chevron-right text-muted fs-5"></i>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ))
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default KaliteOzeti;